import React, { useEffect, useRef } from "react";

/**
 * MatrixRain — HUD-blue variant of the classic falling-code effect.
 *
 * Aviation flavor: a fraction of the columns spell real aviation terms
 * (FAA acronyms, waypoints, ICAO codes) letter-by-letter as they fall,
 * so patient eyes catch "KTEB", "CPDLC", "GOOFY1", "MAYDAY", "SID STAR"
 * scrolling past the rest of the noise.
 *
 * Canvas-based, retina-aware, cleans itself up on unmount.
 */

// Big pool of recognizable aviation strings — waypoints, ICAO airports,
// FAA regs, acronyms, common phraseology. Spaces are preserved and rendered
// as blanks in the strand so multi-word phrases look natural falling.
const AV_WORDS = [
  // Waypoints / SIDs / STARs
  "GOOFY1", "HUXKY", "LOZTT", "JAIKE", "SBJ", "RBV", "COATE", "PARKE", "LANNA",
  "BIGGY", "DIXIE", "DYLIN", "MERIT", "ROBER", "SHIPP", "WHITE", "GREKI",
  "ARD", "LENDY", "NELIE", "SAWED", "SPUDS", "TIGER", "VALRE", "HAAYS",
  // ICAO airports (US + intl)
  "KTEB", "KOPF", "KJFK", "KLGA", "KEWR", "KLAX", "KLAS", "KMIA", "KDAL",
  "KHOU", "KIAH", "KDEN", "KORD", "KATL", "KBWI", "KIAD", "KDCA", "KPWK",
  "KAPA", "KVNY", "KHPN", "KBED", "KPBI", "KFXE", "KFRG", "KSNA", "KFLL",
  "EGLL", "EGGW", "EGKB", "LFPG", "LFPB", "LFMD", "EDDF", "EDDM", "LSGG",
  "VHHH", "RJTT", "RJAA", "OMDB", "LTFM", "YSSY",
  // FAA / phraseology
  "FAA", "TCAS", "ADSB", "IFR", "VFR", "METAR", "TAF", "NOTAM", "RVSM",
  "RNAV", "WAAS", "LPV", "PBN", "RVR", "ILS", "VOR", "DME", "LOC", "NDB",
  "ATIS", "CTAF", "UNICOM", "MAYDAY", "XPDR", "SQUAWK", "CPDLC", "PDC",
  "CLNC DEL", "GND", "TWR", "APP", "DEP", "CTR", "OCEANIC",
  // Regs / manuals
  "FAR91", "FAR135", "FAR121", "POH", "AFM", "MEL", "MMEL",
  // Performance / systems
  "MTOW", "ZFW", "TOGA", "EPR", "N1", "VNAV", "LNAV", "SID", "STAR",
  "IAF", "IAP", "MAP", "DA", "DH", "MDA", "V1", "VR", "V2", "VREF",
  // PJP flavor
  "PJP", "HOT MIC", "JUMPSEAT", "AEROINTEL",
];

export default function MatrixRain({
  color = "#6DB4FF",
  headColor = "#F1F7FF",
  fontSize = 16,
  fadeAlpha = 0.03,
  speed = 8,
  wordProbability = 0.28,
  className = "",
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // Random glyph pool used when a column is NOT spelling a word
    const noise =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>/*+-=|ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍ";

    // Each column state:
    // { y: current head row (float),
    //   tail: array of last-N letters emitted (most recent at index 0),
    //   word: string being spelled, or null,
    //   wordIndex: char index into `word` }
    let width = 0;
    let height = 0;
    let columns = 0;
    let cols = [];
    let frame = 0;
    let rafId = 0;

    const TAIL_LEN = 22; // how many rows of trail we render per column

    const pickWord = () => AV_WORDS[Math.floor(Math.random() * AV_WORDS.length)];
    const pickNoise = () => noise.charAt(Math.floor(Math.random() * noise.length));

    const makeCol = () => ({
      y: Math.random() * -30,
      tail: [],
      word: Math.random() < wordProbability ? pickWord() : null,
      wordIndex: 0,
    });

    const resize = () => {
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = Math.ceil(width / fontSize);
      cols = new Array(columns).fill(0).map(makeCol);
    };

    const advanceColumn = (c) => {
      // Pick next head letter
      let nextChar;
      if (c.word && c.wordIndex < c.word.length) {
        nextChar = c.word.charAt(c.wordIndex);
        c.wordIndex += 1;
      } else {
        // If word just finished, add a small gap of noise before re-picking
        if (c.word && c.wordIndex >= c.word.length) {
          // 40% chance to immediately queue another word, else drift on noise
          if (Math.random() < 0.4) {
            c.word = pickWord();
            c.wordIndex = 0;
            nextChar = c.word.charAt(0);
            c.wordIndex = 1;
          } else {
            c.word = null;
            c.wordIndex = 0;
            nextChar = pickNoise();
          }
        } else {
          nextChar = pickNoise();
        }
      }
      c.tail.unshift(nextChar);
      if (c.tail.length > TAIL_LEN) c.tail.length = TAIL_LEN;
      c.y += 1;
    };

    const draw = () => {
      // Persistent trail fade
      ctx.fillStyle = `rgba(5, 8, 16, ${fadeAlpha})`;
      ctx.fillRect(0, 0, width, height);

      ctx.font = `600 ${fontSize}px "JetBrains Mono", "Fira Code", monospace`;
      ctx.textBaseline = "top";

      const shouldAdvance = frame % speed === 0;

      for (let i = 0; i < columns; i++) {
        const c = cols[i];
        if (shouldAdvance) advanceColumn(c);

        const x = i * fontSize;
        const headRow = Math.floor(c.y);

        // Draw trail from head upward
        for (let t = 0; t < c.tail.length; t++) {
          const row = headRow - t;
          if (row < 0) continue;
          const y = row * fontSize;
          if (y > height) continue;

          const ch = c.tail[t];
          if (t === 0) {
            // Bright head with subtle glow
            ctx.shadowColor = headColor;
            ctx.shadowBlur = 8;
            ctx.fillStyle = headColor;
          } else {
            ctx.shadowBlur = 0;
            // Fade from full color to ~15% alpha along the trail
            const alpha = Math.max(0.12, 1 - t / (TAIL_LEN * 0.9));
            ctx.fillStyle = withAlpha(color, alpha);
          }
          ctx.fillText(ch, x, y);
        }
        ctx.shadowBlur = 0;

        // Reset column when its head is past the bottom (with some randomness
        // so all columns don't restart in lockstep)
        if (headRow * fontSize > height && Math.random() > 0.965) {
          cols[i] = makeCol();
        }
      }

      frame += 1;
      rafId = requestAnimationFrame(draw);
    };

    resize();
    draw();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [color, headColor, fontSize, fadeAlpha, speed, wordProbability]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="matrix-rain"
      aria-hidden="true"
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    />
  );
}

/** Convert #RRGGBB to rgba() with the given alpha. */
function withAlpha(hex, alpha) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
